import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { fetchSalaryById, updateSalary } from '../reducers/SalarySlice';
import { toast } from 'react-toastify';

const UpdateSalary = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { salary, status } = useSelector(state => state.salaries);
  const [loading, setLoading] = useState(false);

  const [formData, setFormData] = useState({
    employeeType: '',
    fullName: '',
    employeeId: '',
    salaryMonth: '',
    basicSalary: 0,
    allowances: 0,
    deductions: 0,
    netSalary: 0,
    paymentDate: '',
  });

  useEffect(() => {
    if (id) {
      dispatch(fetchSalaryById(id));
    }
  }, [id, dispatch]);

  useEffect(() => {
    if (salary) {
      setFormData({
        employeeType: salary.employeeType || '',
        fullName: salary.fullName || '',
        employeeId: salary.employeeId || '',
        basicSalary: salary.basicSalary || 0,
        allowances: salary.allowances || 0,
        deductions: salary.deductions || 0,
        netSalary: salary.basicSalary + salary.allowances - salary.deductions || 0,
      });
    }
  }, [salary]);

  const handleInputChange = (e) => {
    const { id, value } = e.target;

    setFormData((prevState) => {
      const updatedState = { ...prevState, [id]: value };

      if (id === 'basicSalary' || id === 'allowances' || id === 'deductions') {
        updatedState.netSalary =
          Number(updatedState.basicSalary) + Number(updatedState.allowances) - Number(updatedState.deductions);
      }

      return updatedState;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Fetch the existing salary record
      const existingSalary = await dispatch(fetchSalaryById(id)).unwrap(); // Ensure you have this action

      // Create a new salary history record
      const newSalaryHistoryRecord = {
        basicSalary: formData.basicSalary,
        allowances: formData.allowances,
        deductions: formData.deductions,
        netSalary: formData.netSalary,
        salaryMonth: formData.salaryMonth,
        paymentDate: formData.paymentDate || new Date(),
      };

      // Append the new history record to the existing salary history
      const updatedSalaryHistory = [...existingSalary.salaryHistory, newSalaryHistoryRecord];

      // Prepare the updated salary data
      const updatedSalaryData = {
        ...existingSalary, // Keep existing data
        salaryHistory: updatedSalaryHistory, // Update history only
      };

      // Dispatch update action
      const response = await dispatch(updateSalary({ id, salaryData: updatedSalaryData }));

      if (response.payload) {
        toast.success('Salary updated successfully!');
        navigate('/salaries');
      } else {
        toast.error('Error updating salary.');
      }
    } catch (error) {
      toast.error('Update failed!');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container mt-4 text-white">
      <h2 className="mb-4 text-center">Update Salary Data</h2>
      <form onSubmit={handleSubmit}>
        <div className="row mb-3">
          <div className="col-md-4">
            <label htmlFor="fullName" className="form-label">Employee Name</label>
            <input
              type="text"
              className="form-control"
              id="fullName"
              value={formData.fullName}
              onChange={handleInputChange}
              readOnly
            />
          </div>

          <div className="col-md-4">
            <label htmlFor="employeeType" className="form-label">Employee Type</label>
            <input
              type="text"
              className="form-control"
              id="employeeType"
              value={formData.employeeType}
              onChange={handleInputChange}
              readOnly
            />
          </div>
          <div className="col-md-4">
            <label htmlFor="employeeId" className="form-label">Employee ID</label>
            <input
              type="text"
              className="form-control"
              id="employeeId"
              value={formData.employeeId}
              onChange={handleInputChange}
              disabled
            />
          </div>
          <div className="col-md-4">
            <label htmlFor="salaryMonth" className="form-label">New Salary Month</label>
            <input
              type="month"
              className="form-control"
              id="salaryMonth"
              value={formData.salaryMonth}
              onChange={handleInputChange}
            />
          </div>
        </div>
        <div className="row mb-3">
          <div className="col-md-4">
            <label htmlFor="basicSalary" className="form-label">Basic Salary</label>
            <input
              type="number"
              className="form-control"
              id="basicSalary"
              value={formData.basicSalary}
              onChange={handleInputChange}
            />
          </div>
          <div className="col-md-4">
            <label htmlFor="allowances" className="form-label">Allowances</label>
            <input
              type="number"
              className="form-control"
              id="allowances"
              value={formData.allowances}
              onChange={handleInputChange}
            />
          </div>
          <div className="col-md-4">
            <label htmlFor="deductions" className="form-label">Deductions</label>
            <input
              type="number"
              className="form-control"
              id="deductions"
              value={formData.deductions}
              onChange={handleInputChange}
            />
          </div>
        </div>
        <div className="row mb-3">
          <div className="col-md-4">
            <label htmlFor="netSalary" className="form-label">Net Salary</label>
            <input
              type="number"
              className="form-control"
              id="netSalary"
              value={formData.netSalary}
              readOnly
            />
          </div>
          <div className="col-md-4">
            <label htmlFor="paymentDate" className="form-label">Payment Date</label>
            <input
              type="date"
              className="form-control"
              id="paymentDate"
              value={formData.paymentDate ? new Date(formData.paymentDate).toISOString().split('T')[0] : ''}
              onChange={handleInputChange}
            />
          </div>
        </div>
        <div className="row">
          <div className="col-12 d-flex justify-content-end">
            <button
              type="submit"
              className="btn btn-primary text-white"
              disabled={loading}
            >
              {loading ? (
                <span>
                  <span className="spinner-border spinner-border-sm me-2"></span>
                  Updating...
                </span>
              ) : (
                'Update'
              )}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default UpdateSalary;
